import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import ExportButton from '@/components/ExportButton';
import FullScreenButton from '@/components/FullScreenButton';
import { SectionHeading, SrOnly } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import SearchBar from '@/components/SearchBar';
import {
  ShowMoreButton,
  SortableTh,
  TableContent,
  Th,
} from '@/components/Table';
import ViewOptionsModal from '@/components/ViewOptionsModal';
import useDidUpdate from '@/hooks/useDidUpdate';
import { StyledTable } from '@/pages/DistributionVideoSummary/components/DistributionVideoTable';
import { selectDistributionChannelsFilter } from '@/selectors';
import {
  DistributionProjectResource,
  PaginatedResponse,
  SortState,
} from '@/types/resources';
import { api } from '@/utils/api';
import { SortDirection } from '@/utils/enums';
import { RootState } from '@/utils/store';

import DistributionChannelsFilter from './DistributionChannelsFilter';
import DistributionProjectTableRow from './DistributionProjectTableRow';

const DEFAULT_SORT_STATE: SortState = {
  direction: SortDirection.DESC,
  property: 'lastReleaseDate',
};

const columns = [
  {
    title: 'Project ID',
    toggable: false,
    defaultVisibility: true,
  },
  {
    title: 'Thumbnail',
    toggable: false,
    defaultVisibility: true,
  },
  {
    title: 'Video Title',
    toggable: false,
    defaultVisibility: true,
  },
  {
    title: 'Fresh ID',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Production Team',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Last Release Date',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Releases',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Last Release Views',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Best Release Views',
    toggable: true,
    defaultVisibility: true,
  },
  // {
  //   title: "Title Topics",
  //   toggable: true,
  //   defaultVisibility: true,
  // },
  // {
  //   title: "Video Types",
  //   toggable: true,
  //   defaultVisibility: true,
  // },
];

const ProjectStyledTable = styled(StyledTable)`
  td,
  th {
    &:nth-child(3) {
      min-width: 144px;
    }
    &:last-child {
      padding-right: 24px;
      position: static;
    }
  }
`;

const ButtonsHolder = styled.div`
  display: flex;
  margin-bottom: 12px;
  gap: 8px;
  justify-content: flex-end;
`;

const DistributionProjectTable = () => {
  const { teamId } = useParams();

  const [search, setSearch] = useState<string>('');

  const tableViewOptions = useSelector(
    (rootState: RootState) =>
      rootState.options.tableViews['distributionProjectSummaryTable'],
  );

  const [sort, setSort] = useState<SortState>(DEFAULT_SORT_STATE);

  const channelFilterAttributes = useSelector((rootState: RootState) =>
    selectDistributionChannelsFilter(rootState, teamId!),
  );

  const params = {
    // ...(teamId !== "all" && {
    //   "filter[teamId]": teamId,
    // }),
    ...(!!search && {
      'filter[search]': search,
    }),
    ...(channelFilterAttributes?.channelsFrom?.length && {
      'filter[fromChannels]': channelFilterAttributes.channelsFrom
        .map((c) => c.id)
        .join(','),
      periodFrom: channelFilterAttributes.lastReleaseFrom,
    }),
    ...(channelFilterAttributes?.channelsTo?.length && {
      'filter[targetChannels]': channelFilterAttributes.channelsTo
        .map((c) => c.id)
        .join(','),
      periodTo: channelFilterAttributes.lastReleaseTo,
    }),
    ...(sort.direction !== 'DEFAULT' && {
      sort: `${sort.direction === 'DESC' ? '-' : ''}${sort.property}`,
    }),
  };

  const fetchProjects = ({ pageParam = '/v1/distribution-projects' }) =>
    api.get<never, PaginatedResponse<DistributionProjectResource>>(pageParam, {
      params,
    });

  const {
    data,
    isError,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    remove,
    refetch,
  } = useInfiniteQuery<PaginatedResponse<DistributionProjectResource>>(
    [
      'releases',
      {
        sort,
        search,
        // teamId,
        isProjects: true,
      },
    ],
    fetchProjects,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
      staleTime: 60 * 1000,
    },
  );

  // const {
  //   data: projects,
  //   isLoading,
  //   remove,
  //   refetch,
  //   isError,
  // } = useQuery<DistributionProjectResource[]>(
  //   [
  //     "releases",
  //     {
  //       sort,
  //       search,
  //       teamId,
  //       isProjects: true,
  //     },
  //   ],
  //   () =>
  //     api.get<never, DistributionProjectResource[]>(
  //       `/v1/distribution-projects`,
  //       {
  //         params,
  //       }
  //     ),
  //   {
  //     staleTime: 60 * 1000,
  //   }
  // );

  useDidUpdate(() => {
    remove();
    refetch();
  }, [channelFilterAttributes]);

  const fetchCsv = () =>
    api.get<never, string>(`/v1/distribution-projects/export`, {
      params,
    });

  const projects = useMemo(
    () =>
      data?.pages?.reduce<DistributionProjectResource[]>((acc, curr) => {
        return [...acc, ...curr.data];
      }, []),
    [data],
  );

  return (
    <>
      <ButtonsHolder>
        <ExportButton
          fetchCsv={fetchCsv}
          fileName="distribution-projects.csv"
        />
        <ViewOptionsModal
          columns={columns}
          tableKey="distributionProjectSummaryTable"
        />
        <FullScreenButton />
      </ButtonsHolder>
      <DistributionChannelsFilter />
      <SearchBar
        placeholder="Search by Project ID or Fresh ID"
        value={search}
        setValue={setSearch}
      />
      <SectionHeading>Project list</SectionHeading>
      <ScrollableTable>
        <ProjectStyledTable>
          <thead>
            <tr>
              <Th>Project ID</Th>
              {tableViewOptions['Fresh ID'] && <Th>Fresh ID</Th>}
              <Th>Thumbnail</Th>
              <Th>Video Title</Th>
              {tableViewOptions['Production Team'] && (
                <SortableTh
                  property="productionTeam"
                  title="Production Team"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['Last Release Date'] && (
                <SortableTh
                  property="lastReleaseDate"
                  title="Last Release Date"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['Releases'] && (
                <SortableTh
                  property="releasesCount"
                  title="Releases"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['Last Release Views'] && (
                <SortableTh
                  property="lastReleaseViews"
                  title="Last Release Views"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['Best Release Views'] && (
                <SortableTh
                  property="bestReleaseViews"
                  title="Best Release Views"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {/* TODO: check if topics needed */}
              {/* {tableViewOptions["Title Topics"] && <Th>Title Topics</Th>} */}
              {/* {tableViewOptions["Video Types"] && <Th>Video Types</Th>} */}
              <Th $actionsCol>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!projects?.length}
            >
              {projects?.map((project) => (
                <DistributionProjectTableRow
                  key={project.id}
                  project={project}
                />
              ))}
            </TableContent>
          </tbody>
        </ProjectStyledTable>
      </ScrollableTable>
      {hasNextPage && (
        <ShowMoreButton loading={isFetchingNextPage} onClick={fetchNextPage} />
      )}
    </>
  );
};

export default DistributionProjectTable;
